import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '../../contexts/langContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { MDButton } from '../MDButton';
import { MDTypography } from '../MDTypography';
import {
  createBulkDiscountJob,
  pollBulkDiscountJob,
  refreshBulkDiscountDownloadUrl,
} from '../../services/billsService';
import type {
  BulkDiscountJobStatusResponse,
} from '../../BLL/types/bulkDiscountJobs';

const POLL_INTERVAL_MS = 5000;

const useStyles = makeStyles((theme) => ({
  summaryBox: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius, // Fix #9: was hardcoded 4
    marginTop: theme.spacing(2),
  },
  progressBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  progressText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary, // Fix #8: was rgba(0,0,0,0.54)
  },
  progressCaption: {
    color: theme.palette.text.secondary, // Fix #8: was rgba(0,0,0,0.54)
  },
  downloadSection: {
    marginTop: theme.spacing(2),
  },
  refreshButton: {
    marginTop: theme.spacing(1),
  },
  refreshSpinnerBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1), // Fix #8: was inline style={{ gap: 8 }}
  },
  closeButton: {
    color: theme.palette.text.primary,   // Fix #8: was inline style={{ color: 'black' }}
    borderColor: theme.palette.text.primary, // Fix #8: was inline style={{ borderColor: 'black' }}
  },
  actions: {
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
  },
  // Fix #3: visually-hidden aria-live container always in DOM so the browser
  // registers it as a live region before content changes. Screen readers will
  // announce phase transitions (submitting, polling) without the box being visible.
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  },
}));

type DialogPhase =
  | 'idle'
  | 'submitting'
  | 'polling'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

type BulkDiscountPdfDialogProps = {
  open: boolean;
  onClose: () => void;
  selectedBillIds: string[];
  carrierId: number;
};

export const BulkDiscountPdfDialog: React.FC<BulkDiscountPdfDialogProps> = ({
  open,
  onClose,
  selectedBillIds,
  carrierId,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { dir } = useLanguageContext(); // Fix #6: RTL direction for Hebrew

  const [phase, setPhase] = useState<DialogPhase>('idle');
  const [jobStatus, setJobStatus] = useState<BulkDiscountJobStatusResponse | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Stop polling on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Reset state when dialog opens; stop polling when it closes
  useEffect(() => {
    if (open) {
      setPhase('idle');
      setJobStatus(null);
      setDownloadUrl(null);
      setError(null);
      setIsRefreshing(false);
    } else {
      stopPolling();
    }
  }, [open, stopPolling]);

  const startPolling = useCallback(
    (jobId: string) => {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const status = await pollBulkDiscountJob(jobId);
          setJobStatus(status);

          if (status.status === 'completed' || status.status === 'completed_with_errors') {
            stopPolling();
            setDownloadUrl(status.downloadUrl ?? null);
            setPhase(status.status);
          } else if (status.status === 'failed') {
            stopPolling();
            setPhase('failed');
            setError(t('bulkDiscountPdf.jobFailed'));
          }
          // 'pending' and 'processing' → keep polling
        } catch {
          stopPolling();
          setPhase('failed');
          setError(t('bulkDiscountPdf.pollError'));
        }
      }, POLL_INTERVAL_MS);
    },
    [stopPolling, t]
  );

  // Fix #7: useCallback prevents stale closure over selectedBillIds / carrierId props
  const handleGenerate = useCallback(async () => {
    setPhase('submitting');
    setError(null);
    const idempotencyKey = crypto.randomUUID();
    try {
      const response = await createBulkDiscountJob(selectedBillIds, carrierId, idempotencyKey);
      setPhase('polling');
      startPolling(response.jobId);
    } catch {
      setPhase('failed');
      setError(t('bulkDiscountPdf.createError'));
    }
  }, [selectedBillIds, carrierId, startPolling, t]);

  const handleRetry = useCallback(() => {
    setPhase('idle');
    setError(null);
    setJobStatus(null);
    setDownloadUrl(null);
  }, []);

  const handleRefreshUrl = useCallback(async () => {
    if (!jobStatus?.jobId) return;
    setIsRefreshing(true);
    setError(null);
    try {
      const result = await refreshBulkDiscountDownloadUrl(jobStatus.jobId);
      setDownloadUrl(result.downloadUrl);
    } catch {
      setError(t('bulkDiscountPdf.refreshUrlError'));
    } finally {
      setIsRefreshing(false);
    }
  }, [jobStatus?.jobId, t]);

  // Fix #5: replaced handleDownload + window.open with a proper <a> tag in JSX
  // (see Download ZIP button below) — gives link semantics, right-click Save As,
  // middle-click, and correct screen-reader announcement.

  const handleClose = useCallback(() => {
    if (phase === 'submitting') return; // prevent accidental close while creating job
    stopPolling();
    onClose();
  }, [phase, stopPolling, onClose]);

  const isTerminalPhase = phase === 'completed' || phase === 'completed_with_errors' || phase === 'failed';
  const isCompletedPhase = phase === 'completed' || phase === 'completed_with_errors';

  return (
    // Fix #1: aria-labelledby connects the dialog to its title for screen readers (WCAG 4.1.2)
    // Fix #6: PaperProps dir applies RTL layout when the app language is Hebrew
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="bulk-discount-pdf-dialog-title"
      PaperProps={{ dir }}
    >
      <DialogTitle id="bulk-discount-pdf-dialog-title">
        {t('bulkDiscountPdf.title')}
      </DialogTitle>

      <DialogContent>
        {/* Selection summary — always shown */}
        <Box mb={1}>
          <MDTypography variant="body1">
            {t('bulkDiscountPdf.selectedCount', { count: selectedBillIds.length })}
          </MDTypography>
        </Box>

        {/*
          Fix #3: aria-live region for phase transitions.
          The container is always in the DOM so the browser registers it as a live
          region BEFORE any content changes — this is required for reliable
          announcements. Content updates as the phase progresses so screen readers
          hear "Submitting job…" then "Generating PDFs, please wait…".
        */}
        <Box role="status" aria-live="polite" className={classes.srOnly}>
          {phase === 'submitting' && t('bulkDiscountPdf.submitting')}
          {phase === 'polling' && t('bulkDiscountPdf.processing')}
        </Box>

        {/* In-progress states */}
        {(phase === 'submitting' || phase === 'polling') && (
          <Box className={classes.progressBox}>
            {/* Fix #2: aria-label gives screen readers context for what is being processed */}
            <CircularProgress
              aria-label={
                phase === 'submitting'
                  ? t('bulkDiscountPdf.submitting')
                  : t('bulkDiscountPdf.processing')
              }
            />
            <Box className={classes.progressText}>
              {/* Fix #8: color moved to progressText class (was inline rgba) */}
              <MDTypography variant="body2" className={classes.progressText}>
                {phase === 'submitting'
                  ? t('bulkDiscountPdf.submitting')
                  : t('bulkDiscountPdf.processing')}
              </MDTypography>
            </Box>
            {phase === 'polling' && jobStatus && (
              <Box mt={1}>
                {/* Fix #8: color moved to progressCaption class (was inline rgba) */}
                <MDTypography variant="caption" className={classes.progressCaption}>
                  {t('bulkDiscountPdf.progressCount', {
                    completed: jobStatus.completedItemCount,
                    failed: jobStatus.failedItemCount,
                    total: jobStatus.requestedCount,
                  })}
                </MDTypography>
              </Box>
            )}
          </Box>
        )}

        {/* Completed / Completed with errors */}
        {isCompletedPhase && jobStatus && (
          <Box>
            {phase === 'completed' && (
              <Alert severity="success">
                {t('bulkDiscountPdf.completed', { total: jobStatus.requestedCount })}
              </Alert>
            )}
            {phase === 'completed_with_errors' && (
              <Alert severity="warning">
                {t('bulkDiscountPdf.completedWithErrors', {
                  failed: jobStatus.failedItemCount,
                  total: jobStatus.requestedCount,
                })}
              </Alert>
            )}

            <Box className={classes.summaryBox}>
              <MDTypography variant="body2">
                <strong>{t('bulkDiscountPdf.successCount')}:</strong>{' '}
                {jobStatus.completedItemCount}
              </MDTypography>
              <MDTypography variant="body2">
                <strong>{t('bulkDiscountPdf.failedCount')}:</strong>{' '}
                {jobStatus.failedItemCount}
              </MDTypography>
              <MDTypography variant="body2">
                <strong>{t('bulkDiscountPdf.totalCount')}:</strong>{' '}
                {jobStatus.requestedCount}
              </MDTypography>
            </Box>

            {downloadUrl && (
              <Box className={classes.downloadSection}>
                {/*
                  Fix #5: MUI Button renders as <a> when given href, giving link semantics:
                  right-click → Save As, middle-click, keyboard Enter, and screen readers
                  announce "link" not "button". Spread `as any` is required because
                  MDButton's forwardRef is typed to HTMLButtonElement — same limitation
                  that makes MDButtonRoot itself use @ts-ignore internally.
                */}
                <MDButton
                  variant="gradient"
                  color="success"
                  fullWidth
                  {...({ href: downloadUrl, target: '_blank', rel: 'noopener noreferrer' } as any)}
                >
                  {t('bulkDiscountPdf.downloadZip')}
                </MDButton>

                <Box className={classes.refreshButton}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    onClick={handleRefreshUrl}
                    disabled={isRefreshing}
                    fullWidth
                  >
                    {isRefreshing ? (
                      /* Fix #2 + #8: aria-label on spinner; gap via class (was inline style) */
                      <Box className={classes.refreshSpinnerBox}>
                        <CircularProgress size={16} color="inherit" aria-label={t('bulkDiscountPdf.refreshLink')} />
                        {t('bulkDiscountPdf.refreshLink')}
                      </Box>
                    ) : (
                      t('bulkDiscountPdf.refreshLink')
                    )}
                  </MDButton>
                </Box>
              </Box>
            )}

            {error && (
              <Box mt={1}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}
          </Box>
        )}

        {/* Failed state */}
        {phase === 'failed' && (
          <Box mt={1}>
            <Alert severity="error">{error ?? t('bulkDiscountPdf.jobFailed')}</Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Box>
          {phase === 'idle' && (
            <MDButton variant="gradient" color="info" onClick={handleGenerate}>
              {t('bulkDiscountPdf.generateButton')}
            </MDButton>
          )}
          {phase === 'failed' && (
            <MDButton variant="gradient" color="info" onClick={handleRetry}>
              {t('bulkDiscountPdf.retry')}
            </MDButton>
          )}
        </Box>

        {/* Fix #8: color/borderColor moved to closeButton class (was inline style) */}
        <MDButton
          onClick={handleClose}
          disabled={phase === 'submitting'}
          color="white"
          variant="outlined"
          className={classes.closeButton}
        >
          {isTerminalPhase ? t('bulkDiscountPdf.close') : t('bulkDiscountPdf.cancel')}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};
